import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Save, Trash2, ChevronUp, ChevronDown, Eye, Code } from 'lucide-react';
import { useAppStore } from '../store/useAppStore';
import { getProcess, createProcess, updateProcess } from '../services/processService';
import ValidationPicker from '../components/Pickers/ValidationPicker';
import ActionPicker from '../components/Pickers/ActionPicker';
import { ProfileIcon, ProfileMenu, ProfileMenuItem } from '../components/Header/ProfileMenu';
import { FaUserCircle } from 'react-icons/fa';
import { IoSettingsOutline } from 'react-icons/io5';
import { FiLogOut } from 'react-icons/fi';
import Logout from '../components/Logout/Logut';
import useProfileStore from '../store/useProfileStore';
import logo from '../assets/images/newLogo.png';

// Step Row Component
const StepRow = ({ step, index, process, updateStep, deleteStep, moveStep, openPicker }) => {
  return (
    <tr className="hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-purple-50/50 transition-all duration-300 group">
      {/* Step Number */}
      <td className="px-6 py-8">
        <div className="flex flex-col items-center gap-3">
          <div className="relative">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 via-blue-600 to-purple-600 text-white flex items-center justify-center font-extrabold text-lg shadow-xl group-hover:scale-110 transition-transform duration-300">
              {index + 1}
            </div>
            <div className="absolute inset-0 bg-blue-400 rounded-2xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity"></div>
          </div>
          <div className="flex gap-1">
            {index > 0 && (
              <button
                onClick={() => moveStep(step.id, 'up')}
                className="p-1.5 hover:bg-indigo-100 rounded-lg transition-all duration-200 hover:scale-110"
                title="Move up"
              >
                <ChevronUp size={16} className="text-gray-600 hover:text-indigo-700" />
              </button>
            )}
            {index < process.steps.length - 1 && (
              <button
                onClick={() => moveStep(step.id, 'down')}
                className="p-1.5 hover:bg-indigo-100 rounded-lg transition-all duration-200 hover:scale-110"
                title="Move down"
              >
                <ChevronDown size={16} className="text-gray-600 hover:text-indigo-700" />
              </button>
            )}
          </div>
        </div>
      </td>

      {/* Pre-Condition */}
      <td className="px-6 py-8 bg-gradient-to-br from-orange-50/60 to-orange-100/40 group-hover:from-orange-100/70 group-hover:to-orange-200/50 transition-all duration-300">
        <PreConditionCell step={step} process={process} updateStep={updateStep} openPicker={openPicker} />
      </td>

      {/* Step Content */}
      <td className="px-6 py-8 bg-gradient-to-br from-blue-50/60 to-blue-100/40 group-hover:from-blue-100/70 group-hover:to-blue-200/50 transition-all duration-300">
        <StepContentCell step={step} updateStep={updateStep} />
      </td>

      {/* Post-Condition */}
      <td className="px-6 py-8 bg-gradient-to-br from-purple-50/60 to-purple-100/40 group-hover:from-purple-100/70 group-hover:to-purple-200/50 transition-all duration-300">
        <PostConditionCell step={step} process={process} updateStep={updateStep} openPicker={openPicker} />
      </td>

      {/* Actions */}
      <td className="px-6 py-8 text-center bg-gray-50/70 group-hover:bg-gray-100/80 transition-all duration-300">
        <button
          onClick={() => deleteStep(step.id)}
          className="p-3 text-red-600 hover:bg-gradient-to-br hover:from-red-100 hover:to-red-200 rounded-2xl transition-all duration-300 hover:scale-125 shadow-md hover:shadow-xl group/btn"
          title="Delete step"
        >
          <Trash2 size={20} className="group-hover/btn:animate-pulse" />
        </button>
      </td>
    </tr>
  );
};

// Pre-Condition Cell Component
const PreConditionCell = ({ step, process, updateStep, openPicker }) => {
  return (
    <div className="space-y-4">
      {/* Show Auto Continue badge or Add Validation button */}
      {!step.preValidation?.validationId ? (
        <div className="space-y-3">
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border-2 border-green-300">
            <p className="text-sm font-semibold text-green-800 text-center">
              ✓ Auto Continue (No Condition)
            </p>
            <p className="text-xs text-gray-600 text-center mt-1">
              Step will execute without pre-validation
            </p>
          </div>
          
          {/* Pre-Actions button (always available even without validation) */}
          <button
            onClick={() => openPicker('action', step.id, 'preActions')}
            className="w-full text-xs px-4 py-3 bg-gradient-to-r from-yellow-100 via-yellow-200 to-amber-200 border-2 border-yellow-500 rounded-xl hover:from-yellow-200 hover:via-yellow-300 hover:to-amber-300 transition-all duration-300 font-extrabold text-yellow-900 shadow-lg hover:shadow-xl hover:scale-105 transform"
          >
            {step.preValidation?.preActions?.length > 0 
              ? `⚡ ${step.preValidation.preActions.length} Pre-Action(s)` 
              : '+ Add Pre-Actions (execute before step)'}
          </button>
          
          <button
            onClick={() => openPicker('validation', step.id, 'preValidation')}
            className="w-full text-left px-5 py-4 border-3 border-dashed border-orange-400 rounded-2xl hover:border-orange-600 hover:bg-gradient-to-br hover:from-orange-50 hover:to-orange-100 transition-all duration-300 group shadow-lg hover:shadow-2xl hover:scale-105 transform"
          >
            <div className="text-sm text-gray-500 group-hover:text-orange-800 font-bold text-center py-2 flex items-center justify-center gap-2">
              <Plus size={16} />
              Add Pre-Validation (Optional)
            </div>
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <button
              onClick={() => openPicker('validation', step.id, 'preValidation')}
              className="flex-1 text-left px-5 py-4 border-3 border-dashed border-orange-400 rounded-2xl hover:border-orange-600 hover:bg-gradient-to-br hover:from-orange-50 hover:to-orange-100 transition-all duration-300 group shadow-lg hover:shadow-2xl hover:scale-105 transform"
            >
              <div className="text-sm font-bold text-gray-900 mb-1 flex items-center gap-2">
                <span className="text-orange-600">🔍</span>
                {step.preValidation.validationJSON?.name || 'Validation'}
              </div>
              <div className="text-xs text-orange-600 font-semibold">✓ Click to change or remove</div>
            </button>
            
            <button
              onClick={() => updateStep(step.id, {
                preValidation: {
                  validationId: null,
                  validationJSON: null,
                  onTrue: 'continue',
                  onTrueTargetStepId: null,
                  onTrueTargetStepLabel: null,
                  onTrueTargetProcessId: null,
                  onFalse: 'exit',
                  onFalseTargetStepId: null,
                  onFalseTargetStepLabel: null,
                  onFalseTargetProcessId: null,
                  preActions: step.preValidation?.preActions || []
                }
              })}
              className="text-xs px-3 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 font-semibold"
            >
              ✕ Remove Condition
            </button>
          </div>
          
          <div className="space-y-3 bg-gradient-to-br from-white via-orange-50/30 to-white rounded-2xl p-4 border-2 border-orange-200 shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-gray-700">Routing Logic</span>
            </div>
            
            {/* TRUE condition */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-extrabold text-green-800 w-20 bg-gradient-to-r from-green-100 to-green-200 px-3 py-2 rounded-lg shadow-sm">✓ TRUE</span>
                <select
                  value={step.preValidation.onTrue}
                  onChange={(e) => updateStep(step.id, {
                    preValidation: { ...step.preValidation, onTrue: e.target.value }
                  })}
                  className="text-xs border-2 border-green-400 rounded-xl px-4 py-2 flex-1 font-bold focus:ring-2 focus:ring-green-500 focus:border-green-600 bg-white shadow-sm hover:shadow-md transition-all"
                >
                  <option value="always">✓ Always (auto-pass)</option>
                  <option value="never">✕ Never (auto-fail)</option>
                  <option value="continue">→ Continue to Step</option>
                  <option value="exit">✕ Exit Process</option>
                  <option value="jumpToStep">↗ Jump to Step</option>
                  <option value="callProcess">📞 Call Another Process</option>
                </select>
              </div>
              
              {/* Jump to Step - Dropdown + Manual Input */}
              {step.preValidation.onTrue === 'jumpToStep' && (
                <div className="ml-22 space-y-2">
                  <select
                    value={step.preValidation.onTrueTargetStepId || ''}
                    onChange={(e) => updateStep(step.id, {
                      preValidation: { 
                        ...step.preValidation, 
                        onTrueTargetStepId: e.target.value,
                        onTrueTargetStepLabel: null
                      }
                    })}
                    className="w-full text-xs border-2 border-indigo-400 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl px-4 py-2 font-bold focus:ring-2 focus:ring-indigo-500 shadow-sm hover:shadow-md transition-all"
                  >
                    <option value="">↓ Select target step...</option>
                    {process.steps.map((s, i) => (
                      s.id !== step.id && (
                        <option key={s.id} value={s.id}>
                          Step {i + 1}: {s.label || 'Untitled'}
                        </option>
                      )
                    ))}
                  </select>
                  
                  <div className="text-center text-xs text-gray-500 font-semibold">OR</div>
                  
                  <input
                    type="text"
                    value={step.preValidation.onTrueTargetStepLabel || ''}
                    onChange={(e) => updateStep(step.id, {
                      preValidation: { 
                        ...step.preValidation, 
                        onTrueTargetStepLabel: e.target.value,
                        onTrueTargetStepId: null
                      }
                    })}
                    placeholder="Enter step label manually..."
                    className="w-full text-xs border-2 border-indigo-400 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl px-4 py-2 font-bold focus:ring-2 focus:ring-indigo-500 shadow-sm hover:shadow-md transition-all"
                  />
                </div>
              )}
              
              {/* Call Another Process */}
              {step.preValidation.onTrue === 'callProcess' && (
                <input
                  type="text"
                  value={step.preValidation.onTrueTargetProcessId || ''}
                  onChange={(e) => updateStep(step.id, {
                    preValidation: { ...step.preValidation, onTrueTargetProcessId: e.target.value }
                  })}
                  placeholder="Enter process name/ID to call..."
                  className="w-full ml-22 text-xs border-2 border-purple-400 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl px-4 py-2 font-bold focus:ring-2 focus:ring-purple-500 shadow-sm hover:shadow-md transition-all"
                />
              )}
            </div>
            
            {/* FALSE condition */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-extrabold text-red-800 w-20 bg-gradient-to-r from-red-100 to-red-200 px-3 py-2 rounded-lg shadow-sm">✕ FALSE</span>
                <select
                  value={step.preValidation.onFalse}
                  onChange={(e) => updateStep(step.id, {
                    preValidation: { ...step.preValidation, onFalse: e.target.value }
                  })}
                  className="text-xs border-2 border-red-400 rounded-xl px-4 py-2 flex-1 font-bold focus:ring-2 focus:ring-red-500 focus:border-red-600 bg-white shadow-sm hover:shadow-md transition-all"
                >
                  <option value="always">✓ Always (auto-pass)</option>
                  <option value="never">✕ Never (auto-fail)</option>
                  <option value="exit">✕ Exit Process</option>
                  <option value="continue">→ Continue to Step</option>
                  <option value="jumpToStep">↗ Jump to Step</option>
                  <option value="callProcess">📞 Call Another Process</option>
                </select>
              </div>
              
              {/* Jump to Step - Dropdown + Manual Input */}
              {step.preValidation.onFalse === 'jumpToStep' && (
                <div className="ml-22 space-y-2">
                  <select
                    value={step.preValidation.onFalseTargetStepId || ''}
                    onChange={(e) => updateStep(step.id, {
                      preValidation: { 
                        ...step.preValidation, 
                        onFalseTargetStepId: e.target.value,
                        onFalseTargetStepLabel: null
                      }
                    })}
                    className="w-full text-xs border-2 border-indigo-400 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl px-4 py-2 font-bold focus:ring-2 focus:ring-indigo-500 shadow-sm hover:shadow-md transition-all"
                  >
                    <option value="">↓ Select target step...</option>
                    {process.steps.map((s, i) => (
                      s.id !== step.id && (
                        <option key={s.id} value={s.id}>
                          Step {i + 1}: {s.label || 'Untitled'}
                        </option>
                      )
                    ))}
                  </select>
                  
                  <div className="text-center text-xs text-gray-500 font-semibold">OR</div>
                  
                  <input
                    type="text"
                    value={step.preValidation.onFalseTargetStepLabel || ''}
                    onChange={(e) => updateStep(step.id, {
                      preValidation: { 
                        ...step.preValidation, 
                        onFalseTargetStepLabel: e.target.value,
                        onFalseTargetStepId: null
                      }
                    })}
                    placeholder="Enter step label manually..."
                    className="w-full text-xs border-2 border-indigo-400 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl px-4 py-2 font-bold focus:ring-2 focus:ring-indigo-500 shadow-sm hover:shadow-md transition-all"
                  />
                </div>
              )}
              
              {/* Call Another Process */}
              {step.preValidation.onFalse === 'callProcess' && (
                <input
                  type="text"
                  value={step.preValidation.onFalseTargetProcessId || ''}
                  onChange={(e) => updateStep(step.id, {
                    preValidation: { ...step.preValidation, onFalseTargetProcessId: e.target.value }
                  })}
                  placeholder="Enter process name/ID to call..."
                  className="w-full ml-22 text-xs border-2 border-purple-400 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl px-4 py-2 font-bold focus:ring-2 focus:ring-purple-500 shadow-sm hover:shadow-md transition-all"
                />
              )}
            </div>
            
            <button
              onClick={() => openPicker('action', step.id, 'preActions')}
              className="w-full text-xs px-4 py-3 bg-gradient-to-r from-yellow-100 via-yellow-200 to-amber-200 border-2 border-yellow-500 rounded-xl hover:from-yellow-200 hover:via-yellow-300 hover:to-amber-300 transition-all duration-300 font-extrabold text-yellow-900 shadow-lg hover:shadow-xl hover:scale-105 transform"
            >
              {step.preValidation.preActions?.length > 0 
                ? `⚡ ${step.preValidation.preActions.length} Pre-Action(s)` 
                : '+ Add Pre-Actions (execute on pass)'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Step Content Cell Component
const StepContentCell = ({ step, updateStep }) => {
  return (
    <div className="space-y-4">
      <input
        type="text"
        value={step.label}
        onChange={(e) => updateStep(step.id, { label: e.target.value })}
        className="w-full font-extrabold text-base text-gray-900 bg-white border-3 border-blue-300 rounded-xl hover:border-blue-500 focus:border-blue-700 focus:ring-4 focus:ring-blue-200 outline-none px-4 py-3 transition-all duration-200 shadow-md focus:shadow-xl"
        placeholder="Step label (required) *"
      />
      <input
        type="text"
        value={step.description}
        onChange={(e) => updateStep(step.id, { description: e.target.value })}
        className="w-full text-sm text-gray-700 bg-white border-2 border-blue-200 rounded-xl hover:border-blue-400 focus:border-blue-600 focus:ring-4 focus:ring-blue-100 outline-none px-4 py-2.5 transition-all duration-200 shadow-sm focus:shadow-lg"
        placeholder="Step description (required) *"
      />
      
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border-2 border-blue-200">
        <p className="text-xs text-gray-700 text-center flex items-center justify-center gap-2">
          <span>💡</span>
          <span>Step content will be configured during process execution</span>
        </p>
      </div>
    </div>
  );
};

// Post-Condition Cell Component
const PostConditionCell = ({ step, process, updateStep, openPicker }) => {
  return (
    <div className="space-y-4">
      {/* Show Auto Continue badge or Add Routing button */}
      {!step.postValidation?.validationId ? (
        <div className="space-y-3">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border-2 border-blue-300">
            <p className="text-sm font-semibold text-blue-800 text-center">
              → Auto Continue to Next Step
            </p>
            <p className="text-xs text-gray-600 text-center mt-1">
              Automatically proceeds to next step after execution
            </p>
          </div>
          
          {/* Post-Actions button (always available even without validation) */}
          <button
            onClick={() => openPicker('action', step.id, 'postActions')}
            className="w-full text-xs px-4 py-3 bg-gradient-to-r from-yellow-100 via-yellow-200 to-amber-200 border-2 border-yellow-500 rounded-xl hover:from-yellow-200 hover:via-yellow-300 hover:to-amber-300 transition-all duration-300 font-extrabold text-yellow-900 shadow-lg hover:shadow-xl hover:scale-105 transform"
          >
            {step.postValidation?.postActions?.length > 0 
              ? `⚡ ${step.postValidation.postActions.length} Post-Action(s)` 
              : '+ Add Post-Actions (execute after step)'}
          </button>
          
          <button
            onClick={() => openPicker('validation', step.id, 'postValidation')}
            className="w-full text-left px-5 py-4 border-3 border-dashed border-purple-400 rounded-2xl hover:border-purple-600 hover:bg-gradient-to-br hover:from-purple-50 hover:to-purple-100 transition-all duration-300 group shadow-lg hover:shadow-2xl hover:scale-105 transform"
          >
            <div className="text-sm text-gray-500 group-hover:text-purple-800 font-bold text-center py-2 flex items-center justify-center gap-2">
              <Plus size={16} />
              Add Routing Logic (Optional)
            </div>
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <button
              onClick={() => openPicker('validation', step.id, 'postValidation')}
              className="flex-1 text-left px-5 py-4 border-3 border-dashed border-purple-400 rounded-2xl hover:border-purple-600 hover:bg-gradient-to-br hover:from-purple-50 hover:to-purple-100 transition-all duration-300 group shadow-lg hover:shadow-2xl hover:scale-105 transform"
            >
              <div className="text-sm font-bold text-gray-900 mb-1 flex items-center gap-2">
                <span className="text-purple-600">🔀</span>
                {step.postValidation.validationJSON?.name || 'Validation'}
              </div>
              <div className="text-xs text-purple-600 font-semibold">✓ Click to change or remove</div>
            </button>
            
            <button
              onClick={() => updateStep(step.id, {
                postValidation: {
                  validationId: null,
                  validationJSON: null,
                  routing: {
                    onTrue: { action: 'continue', targetStepId: null, targetStepLabel: null, targetProcessId: null },
                    onFalse: { action: 'exit', targetStepId: null, targetStepLabel: null, targetProcessId: null }
                  },
                  postActions: step.postValidation?.postActions || []
                }
              })}
              className="text-xs px-3 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 font-semibold"
            >
              ✕ Remove Routing
            </button>
          </div>
          
          <div className="space-y-3 bg-gradient-to-br from-white via-purple-50/30 to-white rounded-2xl p-4 border-2 border-purple-200 shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-gray-700">Routing Logic</span>
            </div>
            
            {/* TRUE condition */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-extrabold text-green-800 w-20 bg-gradient-to-r from-green-100 to-green-200 px-3 py-2 rounded-lg shadow-sm">✓ TRUE</span>
                <select
                  value={step.postValidation.routing.onTrue.action}
                  onChange={(e) => updateStep(step.id, {
                    postValidation: {
                      ...step.postValidation,
                      routing: {
                        ...step.postValidation.routing,
                        onTrue: { action: e.target.value, targetStepId: null, targetStepLabel: null, targetProcessId: null }
                      }
                    }
                  })}
                  className="text-xs border-2 border-green-400 rounded-xl px-4 py-2 flex-1 font-bold focus:ring-2 focus:ring-green-500 focus:border-green-600 bg-white shadow-sm hover:shadow-md transition-all"
                >
                  <option value="always">✓ Always (auto-pass)</option>
                  <option value="never">✕ Never (auto-fail)</option>
                  <option value="continue">→ Continue</option>
                  <option value="complete">✓ Complete Process</option>
                  <option value="exit">✕ Exit Process</option>
                  <option value="jumpToStep">↗ Jump to Step</option>
                  <option value="callProcess">📞 Call Another Process</option>
                </select>
              </div>
              
              {/* Jump to Step - Dropdown + Manual Input */}
              {step.postValidation.routing.onTrue.action === 'jumpToStep' && (
                <div className="ml-22 space-y-2">
                  <select
                    value={step.postValidation.routing.onTrue.targetStepId || ''}
                    onChange={(e) => updateStep(step.id, {
                      postValidation: {
                        ...step.postValidation,
                        routing: {
                          ...step.postValidation.routing,
                          onTrue: { 
                            ...step.postValidation.routing.onTrue, 
                            targetStepId: e.target.value,
                            targetStepLabel: null
                          }
                        }
                      }
                    })}
                    className="w-full text-xs border-2 border-indigo-400 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl px-4 py-2 font-bold focus:ring-2 focus:ring-indigo-500 shadow-sm hover:shadow-md transition-all"
                  >
                    <option value="">↓ Select target step...</option>
                    {process.steps.map((s, i) => (
                      s.id !== step.id && (
                        <option key={s.id} value={s.id}>
                          Step {i + 1}: {s.label || 'Untitled'}
                        </option>
                      )
                    ))}
                  </select>
                  
                  <div className="text-center text-xs text-gray-500 font-semibold">OR</div>
                  
                  <input
                    type="text"
                    value={step.postValidation.routing.onTrue.targetStepLabel || ''}
                    onChange={(e) => updateStep(step.id, {
                      postValidation: {
                        ...step.postValidation,
                        routing: {
                          ...step.postValidation.routing,
                          onTrue: { 
                            ...step.postValidation.routing.onTrue, 
                            targetStepLabel: e.target.value,
                            targetStepId: null
                          }
                        }
                      }
                    })}
                    placeholder="Enter step label manually..."
                    className="w-full text-xs border-2 border-indigo-400 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl px-4 py-2 font-bold focus:ring-2 focus:ring-indigo-500 shadow-sm hover:shadow-md transition-all"
                  />
                </div>
              )}
              
              {/* Call Another Process */}
              {step.postValidation.routing.onTrue.action === 'callProcess' && (
                <input
                  type="text"
                  value={step.postValidation.routing.onTrue.targetProcessId || ''}
                  onChange={(e) => updateStep(step.id, {
                    postValidation: {
                      ...step.postValidation,
                      routing: {
                        ...step.postValidation.routing,
                        onTrue: { ...step.postValidation.routing.onTrue, targetProcessId: e.target.value }
                      }
                    }
                  })}
                  placeholder="Enter process name/ID to call..."
                  className="w-full ml-22 text-xs border-2 border-purple-400 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl px-4 py-2 font-bold focus:ring-2 focus:ring-purple-500 shadow-sm hover:shadow-md transition-all"
                />
              )}
            </div>
            
            {/* FALSE condition */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-extrabold text-red-800 w-20 bg-gradient-to-r from-red-100 to-red-200 px-3 py-2 rounded-lg shadow-sm">✕ FALSE</span>
                <select
                  value={step.postValidation.routing.onFalse.action}
                  onChange={(e) => updateStep(step.id, {
                    postValidation: {
                      ...step.postValidation,
                      routing: {
                        ...step.postValidation.routing,
                        onFalse: { action: e.target.value, targetStepId: null, targetStepLabel: null, targetProcessId: null }
                      }
                    }
                  })}
                  className="text-xs border-2 border-red-400 rounded-xl px-4 py-2 flex-1 font-bold focus:ring-2 focus:ring-red-500 focus:border-red-600 bg-white shadow-sm hover:shadow-md transition-all"
                >
                  <option value="always">✓ Always (auto-pass)</option>
                  <option value="never">✕ Never (auto-fail)</option>
                  <option value="exit">✕ Exit Process</option>
                  <option value="continue">→ Continue</option>
                  <option value="complete">✓ Complete Process</option>
                  <option value="jumpToStep">↗ Jump to Step</option>
                  <option value="callProcess">📞 Call Another Process</option>
                </select>
              </div>
              
              {/* Jump to Step - Dropdown + Manual Input */}
              {step.postValidation.routing.onFalse.action === 'jumpToStep' && (
                <div className="ml-22 space-y-2">
                  <select
                    value={step.postValidation.routing.onFalse.targetStepId || ''}
                    onChange={(e) => updateStep(step.id, {
                      postValidation: {
                        ...step.postValidation,
                        routing: {
                          ...step.postValidation.routing,
                          onFalse: { 
                            ...step.postValidation.routing.onFalse, 
                            targetStepId: e.target.value,
                            targetStepLabel: null
                          }
                        }
                      }
                    })}
                    className="w-full text-xs border-2 border-indigo-400 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl px-4 py-2 font-bold focus:ring-2 focus:ring-indigo-500 shadow-sm hover:shadow-md transition-all"
                  >
                    <option value="">↓ Select target step...</option>
                    {process.steps.map((s, i) => (
                      s.id !== step.id && (
                        <option key={s.id} value={s.id}>
                          Step {i + 1}: {s.label || 'Untitled'}
                        </option>
                      )
                    ))}
                  </select>
                  
                  <div className="text-center text-xs text-gray-500 font-semibold">OR</div>
                  
                  <input
                    type="text"
                    value={step.postValidation.routing.onFalse.targetStepLabel || ''}
                    onChange={(e) => updateStep(step.id, {
                      postValidation: {
                        ...step.postValidation,
                        routing: {
                          ...step.postValidation.routing,
                          onFalse: { 
                            ...step.postValidation.routing.onFalse, 
                            targetStepLabel: e.target.value,
                            targetStepId: null
                          }
                        }
                      }
                    })}
                    placeholder="Enter step label manually..."
                    className="w-full text-xs border-2 border-indigo-400 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl px-4 py-2 font-bold focus:ring-2 focus:ring-indigo-500 shadow-sm hover:shadow-md transition-all"
                  />
                </div>
              )}
              
              {/* Call Another Process */}
              {step.postValidation.routing.onFalse.action === 'callProcess' && (
                <input
                  type="text"
                  value={step.postValidation.routing.onFalse.targetProcessId || ''}
                  onChange={(e) => updateStep(step.id, {
                    postValidation: {
                      ...step.postValidation,
                      routing: {
                        ...step.postValidation.routing,
                        onFalse: { ...step.postValidation.routing.onFalse, targetProcessId: e.target.value }
                      }
                    }
                  })}
                  placeholder="Enter process name/ID to call..."
                  className="w-full ml-22 text-xs border-2 border-purple-400 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl px-4 py-2 font-bold focus:ring-2 focus:ring-purple-500 shadow-sm hover:shadow-md transition-all"
                />
              )}
            </div>
            
            <button
              onClick={() => openPicker('action', step.id, 'postActions')}
              className="w-full text-xs px-4 py-3 bg-gradient-to-r from-yellow-100 via-yellow-200 to-amber-200 border-2 border-yellow-500 rounded-xl hover:from-yellow-200 hover:via-yellow-300 hover:to-amber-300 transition-all duration-300 font-extrabold text-yellow-900 shadow-lg hover:shadow-xl hover:scale-105 transform"
            >
              {step.postValidation.postActions?.length > 0 
                ? `⚡ ${step.postValidation.postActions.length} Post-Action(s)` 
                : '+ Add Post-Actions (execute on pass)'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Final Actions Section Component
const FinalActionsSection = ({ finalActions, openPicker, removeFinalAction }) => {
  return (
    <div className="bg-gradient-to-br from-amber-100 via-orange-100 to-yellow-200 rounded-3xl shadow-2xl p-10 border-4 border-amber-400 hover:border-amber-500 transition-all duration-300">
      <div className="flex justify-between items-start mb-8">
        <div>
          <h3 className="text-3xl font-extrabold text-gray-900 flex items-center gap-3 mb-3">
            <span className="text-5xl animate-bounce">🎯</span>
            Final Actions
            <span className="text-red-600 text-2xl animate-pulse">*</span>
          </h3>
          <p className="text-base text-gray-800 font-bold bg-white/40 px-4 py-2 rounded-lg inline-block">
            Actions to execute when process completes successfully (mandatory)
          </p>
        </div>
        <button
          onClick={openPicker}
          className="flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-amber-600 via-orange-600 to-yellow-600 text-white rounded-2xl hover:from-amber-700 hover:via-orange-700 hover:to-yellow-700 transition-all duration-300 font-extrabold shadow-2xl hover:shadow-3xl hover:scale-110 transform"
        >
          <Plus size={22} className="font-bold" />
          Add Final Action
        </button>
      </div>
      
      {finalActions.length === 0 ? (
        <div className="text-center py-16 bg-white/80 rounded-2xl border-3 border-dashed border-amber-500 hover:border-orange-500 transition-all duration-300">
          <div className="text-6xl mb-4 animate-pulse">⚠️</div>
          <p className="text-xl font-extrabold text-gray-800 mb-2">No final actions configured</p>
          <p className="text-base mt-3 text-gray-700 font-semibold bg-yellow-100 px-6 py-2 rounded-lg inline-block">
            At least one action is required before saving
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {finalActions.map((action, idx) => (
            <div key={action.id} className="flex items-center gap-5 p-6 bg-white rounded-2xl border-3 border-amber-400 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 transform group">
              <div className="relative">
                <span className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 via-orange-500 to-yellow-500 text-white flex items-center justify-center text-xl font-extrabold shadow-xl group-hover:scale-110 transition-transform">
                  {idx + 1}
                </span>
                <div className="absolute inset-0 bg-orange-400 rounded-2xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity"></div>
              </div>
              <div className="flex-1">
                <div className="font-extrabold text-lg text-gray-900 mb-1">{action.name}</div>
                {action.description && (
                  <div className="text-sm text-gray-700 mt-1 font-medium">{action.description}</div>
                )}
              </div>
              <button
                onClick={() => removeFinalAction(action.id)}
                className="p-3 text-red-600 hover:bg-gradient-to-br hover:from-red-100 hover:to-red-200 rounded-2xl transition-all duration-300 hover:scale-125 shadow-lg hover:shadow-xl"
              >
                <Trash2 size={22} className="hover:animate-pulse" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ProcessBuilderComplete = () => {
  const { appName, processId } = useParams();
  const { setAppName } = useAppStore();
  const navigate = useNavigate();
  const userImage = useProfileStore((state) => state.userImage);
  
  const [process, setProcess] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showJsonPreview, setShowJsonPreview] = useState(false);
  
  // Picker states
  const [activePicker, setActivePicker] = useState(null); // { type, stepId, field }

  useEffect(() => {
    if (appName) {
      setAppName(appName);
    }
    
    if (processId && processId !== 'new') {
      loadProcess();
    } else {
      setProcess({
        id: null,
        name: 'New Process',
        description: '',
        appName,
        status: 'draft',
        steps: [],
        finalActions: [],
        version: 1
      });
    }
  }, [processId, appName, setAppName]);

  const loadProcess = async () => {
    try {
      const data = await getProcess(appName, processId);
      if (data) {
        setProcess(data);
      }
    } catch (error) {
      console.error('Error loading process:', error);
    }
  };

  const handleSaveProcess = async () => {
    if (!process) return;
    
    // Validate mandatory fields
    const errors = [];
    if (!process.name.trim()) errors.push('Process name is required');
    if (!process.description.trim()) errors.push('Process description is required');
    
    process.steps.forEach((step, idx) => {
      if (!step.label.trim()) errors.push(`Step ${idx + 1}: Label is required`);
      if (!step.description.trim()) errors.push(`Step ${idx + 1}: Description is required`);
    });
    
    if (process.finalActions.length === 0) {
      errors.push('At least one final action is required');
    }
    
    if (errors.length > 0) {
      alert('❌ Validation Errors:\n\n' + errors.join('\n'));
      return;
    }
    
    try {
      setSaving(true);
      if (process.id) {
        await updateProcess(appName, process.id, process);
      } else {
        const created = await createProcess(appName, process);
        setProcess(created);
        navigate(`/dashboard/${appName}/process-builder/${created.id}`, { replace: true });
      }
      alert('✓ Process saved successfully!');
    } catch (error) {
      console.error('Error saving process:', error);
      alert('Failed to save process');
    } finally {
      setSaving(false);
    }
  };

  const addStep = () => {
    const newStep = {
      id: `step_${Date.now()}`,
      label: '',  // Mandatory
      description: '',  // Mandatory
      order: process.steps.length + 1,
      
      preValidation: {
        validationId: null,
        validationJSON: null,
        onTrue: 'continue',
        onTrueTargetStepId: null,
        onTrueTargetStepLabel: null,
        onTrueTargetProcessId: null,
        onFalse: 'exit',
        onFalseTargetStepId: null,
        onFalseTargetStepLabel: null,
        onFalseTargetProcessId: null,
        preActions: []
      },
      
      content: {
        type: 'none',  // Simplified - no page/form selection
        contentId: null,
        contentJSON: null
      },
      
      postValidation: {
        validationId: null,
        validationJSON: null,
        routing: {
          onTrue: { 
            action: 'continue', 
            targetStepId: null,
            targetStepLabel: null,
            targetProcessId: null
          },
          onFalse: { 
            action: 'exit', 
            targetStepId: null,
            targetStepLabel: null,
            targetProcessId: null
          }
        },
        postActions: []
      }
    };
    
    setProcess({
      ...process,
      steps: [...process.steps, newStep]
    });
  };

  const updateStep = (stepId, updates) => {
    setProcess({
      ...process,
      steps: process.steps.map(step =>
        step.id === stepId ? { ...step, ...updates } : step
      )
    });
  };

  const deleteStep = (stepId) => {
    if (!confirm('Delete this step?')) return;
    setProcess({
      ...process,
      steps: process.steps.filter(step => step.id !== stepId)
    });
  };

  const moveStep = (stepId, direction) => {
    const index = process.steps.findIndex(s => s.id === stepId);
    if (direction === 'up' && index > 0) {
      const newSteps = [...process.steps];
      [newSteps[index], newSteps[index - 1]] = [newSteps[index - 1], newSteps[index]];
      setProcess({ ...process, steps: newSteps });
    } else if (direction === 'down' && index < process.steps.length - 1) {
      const newSteps = [...process.steps];
      [newSteps[index], newSteps[index + 1]] = [newSteps[index + 1], newSteps[index]];
      setProcess({ ...process, steps: newSteps });
    }
  };

  const openPicker = (type, stepId, field) => {
    setActivePicker({ type, stepId, field });
  };

  const closePicker = () => {
    setActivePicker(null);
  };

  const handleValidationSelect = (validation) => {
    if (!activePicker) return;
    const { stepId, field } = activePicker;
    
    const step = process.steps.find(s => s.id === stepId);
    if (!step) return;
    
    if (field === 'preValidation') {
      updateStep(stepId, {
        preValidation: {
          ...step.preValidation,
          validationId: validation.id,
          validationJSON: validation
        }
      });
    } else if (field === 'postValidation') {
      updateStep(stepId, {
        postValidation: {
          ...step.postValidation,
          validationId: validation.id,
          validationJSON: validation
        }
      });
    }
    closePicker();
  };

  const handleActionSelect = (actions) => {
    if (!activePicker) return;
    const { stepId, field } = activePicker;
    
    if (stepId === 'final') {
      setProcess({
        ...process,
        finalActions: Array.isArray(actions) ? actions : [actions]
      });
    } else {
      const step = process.steps.find(s => s.id === stepId);
      if (!step) return;
      
      if (field === 'preActions') {
        updateStep(stepId, {
          preValidation: {
            ...step.preValidation,
            preActions: Array.isArray(actions) ? actions : [actions]
          }
        });
      } else if (field === 'postActions') {
        updateStep(stepId, {
          postValidation: {
            ...step.postValidation,
            postActions: Array.isArray(actions) ? actions : [actions]
          }
        });
      }
    }
    closePicker();
  };

  if (!process) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-blue-50 to-purple-50">
      {/* Custom Navigation Bar */}
      <div style={{
        padding: '12px 24px',
        background: '#050618',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        minHeight: '5.25rem',
        position: 'sticky',
        top: 0,
        zIndex: 1000
      }}>
        {/* Left Section: MUDUMBAI Logo */}
        <div 
          style={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            marginRight: '32px'
          }}
          onClick={() => navigate('/')}
        >
          <img 
            src={logo} 
            alt="MUDUMBAI Logo" 
            style={{
              height: '3.5rem',
              width: 'auto',
              objectFit: 'contain'
            }}
          />
        </div>

        {/* Center Section: Page-specific content */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          flex: 1
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              background: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px'
            }}>
              🔄
            </div>
            <div>
              <h1 style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: '700',
                color: '#FFFFFF',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
              }}>
                Process Builder
              </h1>
              <p style={{
                margin: 0,
                fontSize: '12px',
                color: 'rgba(255, 255, 255, 0.8)'
              }}>
                {process?.name || 'Create and configure process workflows'}
              </p>
            </div>
          </div>
        </div>

        {/* Right Section: Page-specific buttons, Go to Dashboard, and Profile */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          {/* Back to Processes Button */}
          <button
            onClick={() => navigate(`/dashboard/${appName}/processes`)}
            style={{
              padding: '8px 14px',
              background: 'rgba(255, 255, 255, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '8px',
              color: '#FFFFFF',
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.3)';
              e.target.style.borderColor = 'rgba(255, 255, 255, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.2)';
              e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
            }}
          >
            <ArrowLeft size={16} />
            <span>Back to Processes</span>
          </button>

          {/* Show/Hide JSON Button */}
          {process && (
            <button
              onClick={() => setShowJsonPreview(!showJsonPreview)}
              style={{
                padding: '8px 14px',
                background: 'rgba(255, 255, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '8px',
                color: '#FFFFFF',
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.3)';
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
              }}
            >
              {showJsonPreview ? <Eye size={16} /> : <Code size={16} />}
              <span>{showJsonPreview ? 'Hide' : 'Show'} JSON</span>
            </button>
          )}

          {/* Save Process Button */}
          {process && (
            <button
              onClick={handleSaveProcess}
              disabled={saving}
              style={{
                padding: '8px 14px',
                background: saving ? 'rgba(34, 197, 94, 0.5)' : 'rgba(34, 197, 94, 0.8)',
                border: '1px solid rgba(34, 197, 94, 0.5)',
                borderRadius: '8px',
                color: '#FFFFFF',
                fontSize: '13px',
                fontWeight: '600',
                cursor: saving ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                opacity: saving ? 0.7 : 1
              }}
              onMouseEnter={(e) => {
                if (!saving) {
                  e.target.style.background = 'rgba(34, 197, 94, 1)';
                  e.target.style.borderColor = 'rgba(34, 197, 94, 0.8)';
                }
              }}
              onMouseLeave={(e) => {
                if (!saving) {
                  e.target.style.background = 'rgba(34, 197, 94, 0.8)';
                  e.target.style.borderColor = 'rgba(34, 197, 94, 0.5)';
                }
              }}
            >
              <Save size={16} />
              <span>{saving ? 'Saving...' : 'Save Process'}</span>
            </button>
          )}

          {/* Go to Dashboard Button */}
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              padding: '8px 14px',
              background: 'rgba(255, 255, 255, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '8px',
              color: '#FFFFFF',
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.3)';
              e.target.style.borderColor = 'rgba(255, 255, 255, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.2)';
              e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
            }}
          >
            <span>🏠</span>
            <span>Go to Dashboard</span>
          </button>

          {/* Profile Icon */}
          <div style={{ marginLeft: '12px' }}>
            <ProfileMenu
              trigger={
                <div style={{
                  width: '2.75rem',
                  height: '2.75rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                }}
                >
                  <ProfileIcon userImage={userImage} />
                </div>
              }
            >
              <ProfileMenuItem onClick={() => navigate('/userprofile')}>
                <FaUserCircle size={22} /> Profile
              </ProfileMenuItem>
              <ProfileMenuItem onClick={() => navigate('/settings')}>
                <IoSettingsOutline size={22} /> Setting
              </ProfileMenuItem>
              <ProfileMenuItem>
                <FiLogOut size={20} /> <Logout />
              </ProfileMenuItem>
            </ProfileMenu>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6">
        <div className="max-w-[1600px] mx-auto">
          {/* Process Header */}
          <div className="mb-8">
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 hover:shadow-2xl transition-all duration-300">
              <div className="flex justify-between items-start mb-6">
                <div className="flex-1">
                  <input
                    type="text"
                    value={process?.name || ''}
                    onChange={(e) => setProcess({ ...process, name: e.target.value })}
                    className="text-4xl font-extrabold w-full border-none outline-none mb-3 focus:bg-gradient-to-r focus:from-blue-50 focus:to-purple-50 px-3 py-2 rounded-lg transition-all duration-200 bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text"
                    placeholder="Process Name *"
                    style={{ WebkitTextFillColor: process?.name ? 'transparent' : '#9CA3AF' }}
                  />
                  <input
                    type="text"
                    value={process?.description || ''}
                    onChange={(e) => setProcess({ ...process, description: e.target.value })}
                    className="text-gray-600 text-lg w-full border-none outline-none focus:bg-gradient-to-r focus:from-gray-50 focus:to-blue-50 px-3 py-2 rounded-lg transition-all duration-200"
                    placeholder="Add a description... *"
                  />
                </div>
              </div>
            
            <div className="flex items-center gap-4 text-sm">
              <span className="px-4 py-2 bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 rounded-full font-bold shadow-sm">
                🔢 {process?.steps?.length || 0} Step{(process?.steps?.length || 0) !== 1 ? 's' : ''}
              </span>
              <span className="px-4 py-2 bg-gradient-to-r from-green-100 to-emerald-200 text-green-800 rounded-full font-bold shadow-sm">
                🎯 {process?.finalActions?.length || 0} Final Action{(process?.finalActions?.length || 0) !== 1 ? 's' : ''}
              </span>
              <span className="text-gray-500 flex items-center gap-2">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                Required fields marked with *
              </span>
            </div>
            </div>
          </div>

        {/* JSON Preview */}
        {showJsonPreview && (
          <div className="bg-gradient-to-br from-gray-900 via-slate-900 to-gray-800 text-emerald-400 rounded-2xl p-6 mb-8 font-mono text-sm overflow-auto max-h-96 shadow-2xl border-2 border-gray-700 hover:border-emerald-500 transition-all duration-300">
            <div className="flex items-center gap-2 mb-3 pb-3 border-b border-gray-700">
              <Code size={16} className="text-emerald-400" />
              <span className="text-gray-400 font-semibold">Process JSON Configuration</span>
            </div>
            <pre className="text-xs leading-relaxed">{JSON.stringify(process, null, 2)}</pre>
          </div>
        )}

        {/* Add Step Button */}
        <div className="mb-6">
          <button
            onClick={addStep}
            className="group flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 transition-all duration-300 font-bold shadow-lg hover:shadow-2xl hover:scale-105 transform"
          >
            <Plus size={20} className="group-hover:rotate-90 transition-transform duration-300" />
            Add New Step
          </button>
        </div>

        {/* Steps Table */}
        {process.steps.length === 0 ? (
          <div className="bg-gradient-to-br from-white via-blue-50 to-purple-50 rounded-2xl shadow-xl p-20 text-center mb-8 border-2 border-dashed border-indigo-300 hover:border-indigo-500 transition-all duration-300">
            <div className="text-gray-400 mb-4">
              <div className="relative inline-block">
                <Plus size={80} className="mx-auto mb-6 opacity-20 text-indigo-400" />
                <div className="absolute inset-0 bg-indigo-200 rounded-full blur-3xl opacity-20 animate-pulse"></div>
              </div>
              <p className="text-2xl font-bold text-gray-700 mb-2">No steps added yet</p>
              <p className="text-base mt-3 text-gray-600 max-w-md mx-auto">Click <span className="font-bold text-indigo-600">"Add New Step"</span> button above to start building your process workflow</p>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden mb-8 border-2 border-gray-200 hover:border-indigo-300 transition-all duration-300">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gradient-to-r from-indigo-100 via-blue-100 to-purple-100 border-b-4 border-indigo-200">
                  <th className="px-6 py-5 text-left text-xs font-extrabold text-gray-800 uppercase tracking-wider w-16">#</th>
                  <th className="px-6 py-5 text-left text-xs font-extrabold text-gray-800 uppercase tracking-wider" style={{width: '28%'}}>
                    <div className="flex items-center gap-2">
                      <span className="inline-block w-3 h-3 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 shadow-sm animate-pulse"></span>
                      Pre-Condition <span className="text-orange-700 font-bold ml-1">(Entry)</span>
                    </div>
                  </th>
                  <th className="px-6 py-5 text-left text-xs font-extrabold text-gray-800 uppercase tracking-wider" style={{width: '28%'}}>
                    <div className="flex items-center gap-2">
                      <span className="inline-block w-3 h-3 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 shadow-sm animate-pulse"></span>
                      Step Content <span className="text-red-600 ml-1 text-lg">*</span>
                    </div>
                  </th>
                  <th className="px-6 py-5 text-left text-xs font-extrabold text-gray-800 uppercase tracking-wider" style={{width: '28%'}}>
                    <div className="flex items-center gap-2">
                      <span className="inline-block w-3 h-3 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 shadow-sm animate-pulse"></span>
                      Post-Condition <span className="text-purple-700 font-bold ml-1">(Routing)</span>
                    </div>
                  </th>
                  <th className="px-6 py-5 text-center text-xs font-extrabold text-gray-800 uppercase tracking-wider w-28">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {process.steps.map((step, index) => (
                  <StepRow
                    key={step.id}
                    step={step}
                    index={index}
                    process={process}
                    updateStep={updateStep}
                    deleteStep={deleteStep}
                    moveStep={moveStep}
                    openPicker={openPicker}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Final Actions Section */}
        <FinalActionsSection
          finalActions={process.finalActions}
          openPicker={() => openPicker('action', 'final', 'finalActions')}
          removeFinalAction={(actionId) => {
            setProcess({
              ...process,
              finalActions: process.finalActions.filter(a => a.id !== actionId)
            });
          }}
        />

        {/* Pickers */}
        {activePicker && activePicker.type === 'validation' && (
          <ValidationPicker
            appName={appName}
            onSelect={handleValidationSelect}
            onClose={closePicker}
          />
        )}
        
        {activePicker && activePicker.type === 'action' && (
          <ActionPicker
            appName={appName}
            onSelect={handleActionSelect}
            onClose={closePicker}
            multiple={true}
          />
        )}
        </div>
      </div>
    </div>
  );
};

export default ProcessBuilderComplete;

